/**
 * Unified Agent Selector Component
 *
 * Displays agents in a grid with their provider modes, execution modes,
 * and specialist types. Supports filtering and selection.
 */
import { useState, useMemo } from 'react'
import { clsx } from 'clsx'
import {
  MagnifyingGlassIcon,
  CpuChipIcon,
  SparklesIcon,
  UserIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  ChartBarIcon,
  TableCellsIcon,
  PresentationChartBarIcon,
  BeakerIcon,
  BookOpenIcon,
  CheckCircleIcon,
  ServerIcon,
  CloudIcon,
  BoltIcon,
  ArrowsRightLeftIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline'
import { UnifiedAgent, AgentProviderMode } from '@/types/unified'

interface UnifiedAgentSelectorProps {
  agents: UnifiedAgent[]
  selectedAgentId?: string
  onSelect: (agent: UnifiedAgent) => void
  multiSelect?: boolean
  selectedAgentIds?: string[]
  onMultiSelect?: (agents: UnifiedAgent[]) => void
  showFilters?: boolean
  loading?: boolean
  className?: string
}

// Specialist type icons
const specialistIcons: Record<string, React.ElementType> = {
  coding: CodeBracketIcon,
  documents: DocumentTextIcon,
  graphs: ChartBarIcon,
  spreadsheets: TableCellsIcon,
  presentations: PresentationChartBarIcon,
  data_analysis: BeakerIcon,
  literature_review: BookOpenIcon,
  general: CpuChipIcon,
}

// Provider mode icons (use string keys for flexibility with API responses)
const providerIcons: Record<string, React.ElementType> = {
  local: ServerIcon,
  api: CloudIcon,
  auto: BoltIcon,
  hybrid: ArrowsRightLeftIcon,
}

// Provider mode colors
const providerColors: Record<string, string> = {
  local: 'text-green-400',
  api: 'text-blue-400',
  auto: 'text-yellow-400',
  hybrid: 'text-purple-400',
}

// Execution mode short labels
const executionLabels: Record<string, string> = {
  direct: 'Direct',
  simple: 'Simple',
  triad: 'Triad',
  full: 'Full',
}

interface FilterState {
  search: string
  category: 'all' | 'ziro' | 'template' | 'custom'
  providerMode: AgentProviderMode | 'all'
  specialistType: string | 'all'
}

export default function UnifiedAgentSelector({
  agents,
  selectedAgentId,
  onSelect,
  multiSelect = false,
  selectedAgentIds = [],
  onMultiSelect,
  showFilters = true,
  loading = false,
  className,
}: UnifiedAgentSelectorProps) {
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    category: 'all',
    providerMode: 'all',
    specialistType: 'all',
  })
  const [showFilterPanel, setShowFilterPanel] = useState(false)

  // Get unique specialist types
  const specialistTypes = useMemo(() => {
    const types = new Set<string>()
    agents.forEach((agent) => {
      if (agent.specialist_type) {
        types.add(agent.specialist_type)
      }
    })
    return Array.from(types).sort()
  }, [agents])

  // Filter agents
  const filteredAgents = useMemo(() => {
    return agents.filter((agent) => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase()
        const matchesName = agent.name.toLowerCase().includes(searchLower)
        const matchesDescription = agent.description?.toLowerCase().includes(searchLower)
        const matchesType = agent.specialist_type?.toLowerCase().includes(searchLower)
        if (!matchesName && !matchesDescription && !matchesType) {
          return false
        }
      }

      // Category filter
      if (filters.category !== 'all') {
        if (filters.category === 'ziro' && !agent.is_ziro_specialist) return false
        if (filters.category === 'template' && !agent.is_template) return false
        if (filters.category === 'custom' && !agent.is_custom) return false
      }

      // Provider mode filter
      if (filters.providerMode !== 'all' && agent.provider_mode !== filters.providerMode) {
        return false
      }

      // Specialist type filter
      if (filters.specialistType !== 'all' && agent.specialist_type !== filters.specialistType) {
        return false
      }

      return true
    })
  }, [agents, filters])

  // Group agents by category
  const groupedAgents = useMemo(() => {
    const ziroAgents = filteredAgents.filter((a) => a.is_ziro_specialist)
    const templateAgents = filteredAgents.filter((a) => a.is_template && !a.is_ziro_specialist)
    const customAgents = filteredAgents.filter((a) => a.is_custom)

    return { ziroAgents, templateAgents, customAgents }
  }, [filteredAgents])

  const handleSelect = (agent: UnifiedAgent) => {
    if (multiSelect && onMultiSelect) {
      const isSelected = selectedAgentIds.includes(agent.id)
      if (isSelected) {
        onMultiSelect(agents.filter((a) => selectedAgentIds.includes(a.id) && a.id !== agent.id))
      } else {
        const newSelection = agents.filter((a) => selectedAgentIds.includes(a.id) || a.id === agent.id)
        onMultiSelect(newSelection)
      }
    } else {
      onSelect(agent)
    }
  }

  const isSelected = (agent: UnifiedAgent) => {
    if (multiSelect) {
      return selectedAgentIds.includes(agent.id)
    }
    return agent.id === selectedAgentId
  }

  const renderAgentCard = (agent: UnifiedAgent) => {
    const SpecialistIcon = agent.specialist_type
      ? specialistIcons[agent.specialist_type] || CpuChipIcon
      : CpuChipIcon
    const providerMode = agent.provider_mode || 'auto'
    const executionMode = agent.execution_mode || 'direct'
    const ProviderIcon = providerIcons[providerMode] || BoltIcon
    const selected = isSelected(agent)

    return (
      <button
        key={agent.id}
        onClick={() => handleSelect(agent)}
        className={clsx(
          'flex flex-col p-4 rounded-lg border-2 transition-all text-left w-full',
          selected
            ? 'bg-primary-900/30 border-primary-600'
            : 'bg-gray-800 border-gray-700 hover:border-gray-600 hover:bg-gray-750'
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center">
            <div
              className={clsx(
                'p-2 rounded-lg mr-3',
                agent.is_ziro_specialist
                  ? 'bg-primary-900/50'
                  : agent.is_custom
                  ? 'bg-purple-900/50'
                  : 'bg-gray-700'
              )}
            >
              {agent.is_ziro_specialist ? (
                <SparklesIcon className="w-5 h-5 text-primary-400" />
              ) : agent.is_custom ? (
                <UserIcon className="w-5 h-5 text-purple-400" />
              ) : (
                <SpecialistIcon className="w-5 h-5 text-gray-400" />
              )}
            </div>
            <div>
              <h4 className={clsx('font-medium', selected ? 'text-white' : 'text-gray-200')}>
                {agent.name}
              </h4>
              {agent.specialist_type && (
                <span className="text-xs text-primary-400 capitalize">
                  {agent.specialist_type.replace('_', ' ')}
                </span>
              )}
            </div>
          </div>

          {selected && (
            <CheckCircleIcon className="w-5 h-5 text-primary-400 flex-shrink-0" />
          )}
        </div>

        {/* Description */}
        {agent.description && (
          <p className="text-xs text-gray-400 mb-3 line-clamp-2">{agent.description}</p>
        )}

        {/* Mode badges */}
        <div className="flex items-center gap-2 mt-auto">
          {/* Provider mode */}
          <div
            className={clsx(
              'flex items-center text-xs px-2 py-0.5 rounded',
              'bg-gray-700',
              providerColors[providerMode] || 'text-gray-400'
            )}
            title={`Provider: ${providerMode}`}
          >
            <ProviderIcon className="w-3 h-3 mr-1" />
            {providerMode}
          </div>

          {/* Execution mode */}
          <div
            className="flex items-center text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-300"
            title={`Execution: ${executionMode}`}
          >
            {executionLabels[executionMode] || executionMode}
          </div>

          {/* Quality badge */}
          {agent.enable_quality_review && (
            <div
              className="text-xs px-2 py-0.5 rounded bg-green-900/30 text-green-400"
              title={`Quality threshold: ${agent.quality_threshold}`}
            >
              QA
            </div>
          )}
        </div>
      </button>
    )
  }

  const renderAgentGroup = (title: string, agents: UnifiedAgent[], icon: React.ElementType) => {
    const Icon = icon
    if (agents.length === 0) return null

    return (
      <div className="mb-6">
        <h3 className="flex items-center text-sm font-medium text-gray-300 mb-3">
          <Icon className="w-4 h-4 mr-2" />
          {title}
          <span className="ml-2 text-xs text-gray-500">({agents.length})</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {agents.map(renderAgentCard)}
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className={clsx('space-y-3', className)}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-gray-800 rounded-lg p-4 animate-pulse">
            <div className="h-4 bg-gray-700 rounded w-1/3 mb-2" />
            <div className="h-3 bg-gray-700 rounded w-2/3" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={clsx('space-y-4', className)}>
      {/* Search and Filters */}
      {showFilters && (
        <div className="space-y-3">
          {/* Search bar */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search agents..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={clsx(
                'flex items-center px-3 py-2 rounded-lg border transition-colors',
                showFilterPanel
                  ? 'bg-primary-900/30 border-primary-600 text-primary-400'
                  : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
              )}
            >
              <FunnelIcon className="w-4 h-4 mr-2" />
              Filters
            </button>
          </div>

          {/* Filter panel */}
          {showFilterPanel && (
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Category filter */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Category</label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters({ ...filters, category: e.target.value as FilterState['category'] })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All Categories</option>
                    <option value="ziro">Ziro Specialists</option>
                    <option value="template">Template Agents</option>
                    <option value="custom">Custom Agents</option>
                  </select>
                </div>

                {/* Provider mode filter */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Provider Mode</label>
                  <select
                    value={filters.providerMode}
                    onChange={(e) => setFilters({ ...filters, providerMode: e.target.value as AgentProviderMode | 'all' })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All Providers</option>
                    <option value="local">Local (Ollama)</option>
                    <option value="api">API (Cloud)</option>
                    <option value="auto">Auto</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>

                {/* Specialist type filter */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Specialist Type</label>
                  <select
                    value={filters.specialistType}
                    onChange={(e) => setFilters({ ...filters, specialistType: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All Types</option>
                    {specialistTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Category quick filters */}
          <div className="flex gap-2">
            {(['all', 'ziro', 'template', 'custom'] as const).map((category) => (
              <button
                key={category}
                onClick={() => setFilters({ ...filters, category })}
                className={clsx(
                  'px-3 py-1.5 text-sm rounded-lg transition-colors',
                  filters.category === category
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                )}
              >
                {category === 'all' && 'All'}
                {category === 'ziro' && 'Ziro'}
                {category === 'template' && 'Template'}
                {category === 'custom' && 'Custom'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Agent count */}
      <div className="text-sm text-gray-400">
        Showing {filteredAgents.length} of {agents.length} agents
        {multiSelect && selectedAgentIds.length > 0 && (
          <span className="ml-2 text-primary-400">
            ({selectedAgentIds.length} selected)
          </span>
        )}
      </div>

      {/* Agent groups */}
      {filters.category === 'all' ? (
        <>
          {renderAgentGroup('Ziro Intelligence Specialists', groupedAgents.ziroAgents, SparklesIcon)}
          {renderAgentGroup('Template Agents', groupedAgents.templateAgents, CpuChipIcon)}
          {renderAgentGroup('Custom Agents', groupedAgents.customAgents, UserIcon)}
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredAgents.map(renderAgentCard)}
        </div>
      )}

      {/* Empty state */}
      {filteredAgents.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <CpuChipIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No agents match your filters</p>
          <button
            onClick={() =>
              setFilters({
                search: '',
                category: 'all',
                providerMode: 'all',
                specialistType: 'all',
              })
            }
            className="mt-2 text-primary-400 hover:text-primary-300 text-sm"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  )
}
